import { useCallback, useEffect, useState } from "react";
import Cropper, { Area } from "react-easy-crop";
import Modal, { ModalRoot } from "../../components/Modal/Modal";
import { Button } from "../../components/Button";
import Spinner from "../../components/graphics/Spinner";

// Pasfoto-forhold: 35 x 45 mm (portræt).
const PASSPORT_ASPECT = 35 / 45;
// Outputbredde i pixels. Beskæringen tegner altid til denne bredde, uanset kildebilledet, og
// det er her kravet til bredden bliver opfyldt. Serveren afviser foto under 300px bredt
// (IdKortLogic), så hold denne værdi over det — så kan kildebilledets bredde aldrig vælte en
// bestilling.
const OUTPUT_WIDTH = 600;
const OUTPUT_HEIGHT = Math.round(OUTPUT_WIDTH / PASSPORT_ASPECT);

const loadImage = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("Billedet kunne ikke indlæses."));
    img.src = src;
  });

/** Tegner det valgte udsnit til et canvas i pasfoto-format og returnerer en ny fil. */
const cropToFile = async (
  imageSrc: string,
  area: Area,
  fileName: string,
): Promise<File> => {
  const image = await loadImage(imageSrc);
  const canvas = document.createElement("canvas");
  canvas.width = OUTPUT_WIDTH;
  canvas.height = OUTPUT_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas understøttes ikke i denne browser.");

  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    image,
    area.x,
    area.y,
    area.width,
    area.height,
    0,
    0,
    OUTPUT_WIDTH,
    OUTPUT_HEIGHT,
  );

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92),
  );
  if (!blob) throw new Error("Fotoet kunne ikke behandles.");

  const baseName = fileName.replace(/\.[^./\\]+$/, "") || "foto";
  return new File([blob], `${baseName}-pasfoto.jpg`, { type: "image/jpeg" });
};

interface PassportCropModalProps {
  /** Kildefil valgt af brugeren. Når den er sat, åbnes modalen. */
  file: File | null;
  onCancel: () => void;
  onConfirm: (croppedFile: File) => void;
}

const PassportCropModal = ({
  file,
  onCancel,
  onConfirm,
}: PassportCropModalProps) => {
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // TODO(react-hooks-7): resets the crop UI whenever a new file arrives. The
  // idiomatic replacement is remounting this modal with key={file.name} so the
  // useState initialisers reset instead of an effect writing six setStates.
  useEffect(() => {
    if (!file) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setImageSrc(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setImageSrc(url);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setError(null);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const onCropComplete = useCallback((_area: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleConfirm = async () => {
    if (!imageSrc || !croppedAreaPixels || !file) return;
    setIsProcessing(true);
    setError(null);
    try {
      const cropped = await cropToFile(imageSrc, croppedAreaPixels, file.name);
      onConfirm(cropped);
    } catch (e) {
      setError(
        e instanceof Error
          ? e.message
          : "Der opstod en fejl under behandling af fotoet.",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <ModalRoot
      controller={{
        open: Boolean(file),
        setOpen: (open) => {
          if (!open && !isProcessing) onCancel();
        },
      }}
    >
      <Modal
        title="Tilpas dit foto"
        description="Beskær dit foto til pasfoto-format"
        footer={
          <div className="d-flex justify-content-end gap-2 p-4">
            <button
              type="button"
              className="btn btn-outline"
              onClick={onCancel}
              disabled={isProcessing}
            >
              Annuller
            </button>
            <Button
              id="idkort-crop-confirm"
              color="primary"
              onClick={handleConfirm}
              disabled={isProcessing || !croppedAreaPixels}
            >
              {isProcessing ? (
                <span className="d-inline-flex align-items-center gap-2">
                  <Spinner inline size={16} label="" />
                  Behandler...
                </span>
              ) : (
                "Brug dette foto"
              )}
            </Button>
          </div>
        }
      >
        <div className="d-flex flex-column gap-3">
          <p className="mb-0 text-muted">
            Træk og zoom, så dit ansigt fylder rammen. Fotoet beskæres
            automatisk til pasfoto-format.
          </p>

          {/* Kravene til motivet står ved fotofeltet på formularen, ikke her:
              her har medlemmet allerede valgt sit billede, og et forkert motiv
              kan ikke beskæres på plads (ADV-245). */}

          {error ? (
            <div className="alert alert-danger mb-0" role="alert">
              {error}
            </div>
          ) : null}

          <div
            style={{
              position: "relative",
              width: "100%",
              // Viger på lave skærme frem for at skubbe zoom-slideren under
              // modalens max-height (100vh - 106,5px), hvor .c-modal-content har
              // overflow-y: auto.
              //
              // Begrænsningen er additiv, ikke proportional: modalens faste dele
              // — header, footer, container-padding, hjælpetekst, zoom-label og
              // slider — fylder ca. 530px uanset vindueshøjden. Derfor calc og
              // ikke en vh-brøk. Målt i browser: ved 760px vindueshøjde fyldte
              // rammen 360px og gav 125px overløb; nu 230px og intet overløb.
              // 360px-loftet betyder, at skærme over ca. 890px beholder den
              // fulde ramme, så kun lave skærme betaler.
              height: "min(360px, calc(100vh - 530px))",
              background: "#333",
              borderRadius: 8,
              overflow: "hidden",
            }}
          >
            {imageSrc ? (
              <Cropper
                image={imageSrc}
                crop={crop}
                zoom={zoom}
                aspect={PASSPORT_ASPECT}
                onCropChange={setCrop}
                onZoomChange={setZoom}
                onCropComplete={onCropComplete}
              />
            ) : null}
          </div>

          <label className="form-label mb-1" htmlFor="idkort-crop-zoom">
            Zoom
          </label>
          <input
            id="idkort-crop-zoom"
            type="range"
            className="form-range"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
          />
        </div>
      </Modal>
    </ModalRoot>
  );
};

export default PassportCropModal;
